import clsx from 'clsx'
import {isEqual, last, size} from 'lodash'
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {convertCurrency, getShipmentFees, KTSVG, StorageHelpers} from '../../../../../_gori/helpers'
import {useStatesGlobal} from '../../../../../_gori/helpers/components/StatesGlobalProvider'
import {OrderStatus} from '../../core/_const'
import {useNavigate, useLocation} from 'react-router-dom'
import {
  derivePinnedRates,
  getCarrierNameFromServiceKey,
  getCarrierService,
  getDeliveryWindowValues,
  isCarrierApplicableForRoute,
  normalizeCheapestRates,
  normalizeRateText,
} from './rateHelpers'
import {EmptyRate} from './EmptyRate'
import {PresetRateSection} from './PresetRateSection'
import {SERVICES, SERVICE_INFO, RATE_SERVICES_KEY} from '../../../../../_gori/constants'

type Props = {
  data: any
  handleClickRate: any
  disabled?: boolean
  dataShipment?: any
  isLoadingRates?: boolean
  hideNoRates?: boolean
  presetService?: string
  disabledCarriers?: string[]
  loadingCarriers?: string[]
  retryingCarriers?: string[]
  canGetRates?: boolean
  isInternational?: boolean
  onFetchCarrierRates?: (carrierName: string) => void
  onRetryCarrierRates?: (carrierName: string) => void
  onExploreAllRates?: () => void
  onFetchActiveCarriers?: () => void
}
const Rates: React.FC<Props> = ({
  data,
  handleClickRate,
  disabled = false,
  dataShipment = null,
  isLoadingRates = false,
  hideNoRates = false,
  presetService,
  disabledCarriers = [],
  loadingCarriers = [],
  retryingCarriers = [],
  canGetRates = true,
  isInternational = false,
  onFetchCarrierRates,
  onRetryCarrierRates,
  onExploreAllRates,
  onFetchActiveCarriers,
}) => {
  const intl = useIntl()
  const {statesGlobal} = useStatesGlobal()
  const carriers = statesGlobal.carriers

  const rateOptions = useMemo(() => {
    return normalizeCheapestRates(data?.options)
  }, [data?.options])

  const matchingPackages = useMemo(() => {
    return rateOptions.filter((item) => {
      return item?.matching_package && Number.isFinite(Number(item?.rate))
    })
  }, [rateOptions])

  const pinnedRates = useMemo(() => derivePinnedRates(rateOptions), [rateOptions])

  const preselectedRate = useMemo(() => {
    if (!presetService) return null
    return (
      rateOptions.find(
        (item) =>
          item?.carrier_service === presetService &&
          !item?.error &&
          Number.isFinite(Number(item?.rate))
      ) || null
    )
  }, [presetService, rateOptions])

  const hasPreselected = !!preselectedRate

  // Whether the preset service matches the current route type
  const isPresetServiceApplicable = useMemo(() => {
    if (!presetService) return true
    const serviceConfig = carriers?.[presetService]
    if (serviceConfig?.international === undefined) return true
    return serviceConfig.international === isInternational
  }, [presetService, carriers, isInternational])

  const [isExpandedAllRates, setIsExpandedAllRates] = useState(false)
  const [hasEverExpanded, setHasEverExpanded] = useState(
    !hasPreselected && isPresetServiceApplicable
  )
  // Tracks whether the user explicitly expanded via "All Rates" in the current session.
  // Prevents collapsing when incremental carrier fetches trigger isLoadingRates.
  const userExpandedRef = useRef(false)
  // Tracks whether the inapplicable preset error has been shown for the current preset.
  // Only resets when a new preset is applied (presetService changes).
  const [inapplicablePresetDismissed, setInapplicablePresetDismissed] = useState(false)

  // Effective preset for display logic — null when inapplicable preset has been dismissed.
  // After dismissal, the rates panel behaves as if no preset was set.
  const activePresetService =
    presetService && (!inapplicablePresetDismissed || isPresetServiceApplicable)
      ? presetService
      : undefined

  const [fetchedCarriers, setFetchedCarriers] = useState<Set<string>>(new Set())
  const [hasExploredAll, setHasExploredAll] = useState(false)
  const [presetTransitioning, setPresetTransitioning] = useState(false)

  const handleFetchCarrierRates = useCallback(
    (carrierName: string) => (e: React.MouseEvent) => {
      e.stopPropagation()
      if (onFetchCarrierRates) {
        onFetchCarrierRates(carrierName)
        setFetchedCarriers((prev) => new Set(prev).add(carrierName))
      }
    },
    [onFetchCarrierRates]
  )

  const handleRetryCarrier = useCallback(
    (carrierName: string) => (e: React.MouseEvent) => {
      e.stopPropagation()
      if (onRetryCarrierRates) {
        onRetryCarrierRates(carrierName)
      }
    },
    [onRetryCarrierRates]
  )

  const renderTimeoutCard = useCallback(
    (item: any) => {
      const carrierName = item?.carrierName
      const isRetrying = carrierName ? loadingCarriers.includes(carrierName) : false

      return (
        <div
          key={item?.carrier_service}
          className='btn border border-secondary d-flex justify-content-between align-items-center my-5 text-start'
        >
          <div className='d-flex align-items-center'>
            {item?.logo && (
              <div className='symbol symbol-40px me-5'>
                <span className='symbol-label bg-transparent'>
                  <img
                    src={item.logo}
                    className='h-100 align-self-center'
                    alt={item?.display_name}
                  />
                </span>
              </div>
            )}
            <div>
              <span className='fs-6 fw-semibold text-gray-800'>{item?.display_name}</span>
              {isRetrying ? (
                <div className='fs-8 text-gray-500'>{intl.formatMessage({id: 'LOADING'})}</div>
              ) : (
                <div className='fs-8 text-gray-500'>
                  {intl.formatMessage(
                    {id: 'CARRIER_RATES_UNAVAILABLE'},
                    {carrier: item?.display_name}
                  )}
                  <div>{intl.formatMessage({id: 'CARRIER_RATES_UNAVAILABLE_SUBTEXT'})}</div>
                </div>
              )}
            </div>
          </div>
          {carrierName && onRetryCarrierRates && (
            <button
              type='button'
              className={clsx('btn btn-sm btn-get-rate', {'pe-none': isRetrying || !canGetRates})}
              onClick={handleRetryCarrier(carrierName)}
              disabled={!canGetRates}
            >
              {isRetrying ? (
                <span className='indicator-progress d-block'>
                  <span className='spinner-border spinner-border-sm align-middle' role='status' />
                </span>
              ) : (
                intl.formatMessage({id: 'RETRY'})
              )}
            </button>
          )}
        </div>
      )
    },
    [intl, loadingCarriers, handleRetryCarrier, onRetryCarrierRates, canGetRates]
  )

  useEffect(() => {
    if (isLoadingRates) {
      // When a full refresh happens (options reset to empty), collapse the expanded view
      // ONLY if the user hasn't explicitly expanded via "All Rates".
      // Once the user has expanded, keep showing all rates even across form-triggered refetches.
      // Note: hasEverExpanded is never reset here — once the user has fetched all rates,
      // the pinned items (cheapest, fastest, best) should remain visible across refetches.
      // It only resets when a new preset is applied (via the presetService effect).
      const isFullRefresh = !data?.options?.length
      if (!userExpandedRef.current && isFullRefresh) {
        setIsExpandedAllRates(false)
      }
      // Mark currently loading carriers as fetched so they don't show "Get Rate" after refresh
      setFetchedCarriers(new Set(loadingCarriers))
      setPresetTransitioning(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingRates])

  // Clear transition when rates have loaded or loading has completed
  useEffect(() => {
    if (presetTransitioning && !isLoadingRates) {
      setPresetTransitioning(false)
    }
  }, [presetTransitioning, isLoadingRates])

  // Reset hasExploredAll when rates are cleared (form invalidation or fresh fetch cycle)
  useEffect(() => {
    if (
      data?.options === undefined ||
      (Array.isArray(data?.options) && data.options.length === 0)
    ) {
      setHasExploredAll(false)
    }
  }, [data?.options])

  // Clear stale fetched carriers when route type changes (domestic ↔ international)
  useEffect(() => {
    setFetchedCarriers(new Set())
  }, [isInternational])

  // Suppress preset sections during transition to prevent flash of stale data.
  // Also collapse expanded rates so only the preset shows after switching.
  useEffect(() => {
    if (presetService) {
      setHasEverExpanded(false)
      setIsExpandedAllRates(false)
      userExpandedRef.current = false
      setInapplicablePresetDismissed(false)
    }
  }, [presetService])

  // When rates are cleared (start of a refetch), suppress preset sections
  // to prevent showing stale preset data from the previous cycle.
  useEffect(() => {
    if (presetService && Array.isArray(data?.options) && data.options.length === 0) {
      setPresetTransitioning(true)
    }
  }, [data?.options, presetService])

  // Track carriers as they start loading so fetchedCarriers stays complete
  // even if the isLoadingRates snapshot misses some due to batched state updates
  useEffect(() => {
    if (loadingCarriers.length > 0) {
      setFetchedCarriers((prev) => {
        let changed = false
        const next = new Set(prev)
        for (const name of loadingCarriers) {
          if (!next.has(name)) {
            next.add(name)
            changed = true
          }
        }
        return changed ? next : prev
      })
    }
  }, [loadingCarriers])

  // Ensure the preset carrier is marked as fetched once loading completes.
  // This handles the case where the preset-only fetch finishes but the
  // loadingCarriers tracking effect missed it due to React batching.
  useEffect(() => {
    const _presetCarrierName = presetService ? getCarrierNameFromServiceKey(presetService) : null
    if (
      _presetCarrierName &&
      !isLoadingRates &&
      !loadingCarriers.includes(_presetCarrierName) &&
      data?.options !== undefined
    ) {
      setFetchedCarriers((prev) => {
        if (prev.has(_presetCarrierName)) return prev
        const next = new Set(prev)
        next.add(_presetCarrierName)
        return next
      })
    }
  }, [presetService, isLoadingRates, loadingCarriers, data?.options])

  const toggleAllRates = useCallback(() => {
    setIsExpandedAllRates((prev) => {
      if (!prev) setHasEverExpanded(true)
      return !prev
    })
  }, [])

  const navigate = useNavigate()
  const location = useLocation()

  // NOTE: data props changes multiple times, then component re renders multiple times
  useEffect(() => {
    const prevData = StorageHelpers.getItemLocalStorage(RATE_SERVICES_KEY)
    if (!rateOptions.length) return
    if (!isEqual(prevData, rateOptions) && !!prevData) {
      navigate(window.location.pathname, {replace: true, state: location.state})
      StorageHelpers.removeItemLocalStorage(RATE_SERVICES_KEY)
    } else {
      StorageHelpers.setItemLocalStorage(RATE_SERVICES_KEY, rateOptions)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rateOptions])

  const renderErrors = useCallback(
    (item) => {
      const errorMessage = normalizeRateText(item?.error, intl.formatMessage({id: 'ERROR'}))

      return (
        <OverlayTrigger
          overlay={
            <Tooltip id='tooltip-disabled'>
              <span className='text-danger'>{errorMessage}</span>
            </Tooltip>
          }
        >
          <span>
            <KTSVG
              path='/media/gori/settings/errors.svg'
              className='ms-2 mb-1 svg-icon-4 cursor-pointer'
            />
          </span>
        </OverlayTrigger>
      )
    },
    [intl]
  )

  const [showUspsFirstDisclaimer, setShowUspsFirstDisclaimer] = React.useState(false)

  const renderEmptyFetchedCard = useCallback(
    (carrierName: string) => {
      const carrierService = getCarrierService(statesGlobal?.carriers, carrierName)
      const logo = carrierService?.visuals?.logo
      return (
        <div
          key={`${carrierName}:empty-fetched`}
          className='btn border border-secondary d-flex justify-content-between align-items-center my-5 text-start pe-none opacity-75'
        >
          <div className='d-flex align-items-center'>
            {logo && (
              <div className='symbol symbol-40px me-5'>
                <span className='symbol-label bg-transparent'>
                  <img src={logo} className='h-100 align-self-center' alt={carrierName} />
                </span>
              </div>
            )}
            <div>
              <span className='fs-6 fw-semibold text-gray-800'>
                {carrierService?.carrier_label || carrierName.toUpperCase()}
              </span>
              <div className='fs-8 text-gray-500'>
                {intl.formatMessage({id: 'NO_RATES_AVAILABLE_CARRIER'})}
              </div>
            </div>
          </div>
        </div>
      )
    },
    [intl, statesGlobal?.carriers]
  )

  const renderDisabledCarrierCard = useCallback(
    (carrierName: string) => {
      const isLoading = loadingCarriers.includes(carrierName)
      const carrierService = getCarrierService(statesGlobal?.carriers, carrierName)
      const logo = carrierService?.visuals?.logo

      return (
        <div
          key={carrierName}
          className='btn border border-secondary d-flex justify-content-between align-items-center my-5 text-start'
        >
          <div className='d-flex align-items-center'>
            {logo && (
              <div className='symbol symbol-40px me-5'>
                <span className='symbol-label bg-transparent'>
                  <img src={logo} className='h-100 align-self-center' alt={carrierName} />
                </span>
              </div>
            )}
            <div className='d-flex align-items-center'>
              <span className='service-name fs-6 fw-semibold text-capitalize'>
                {carrierService?.carrier_label || carrierName.toUpperCase()}
              </span>
            </div>
          </div>
          <button
            type='button'
            className={clsx('btn btn-sm btn-get-rate', {
              'pe-none': isLoading || !canGetRates,
            })}
            onClick={handleFetchCarrierRates(carrierName)}
            disabled={!canGetRates}
          >
            {isLoading ? (
              <span className='indicator-progress d-block'>
                <span className='spinner-border spinner-border-sm align-middle' role='status' />
              </span>
            ) : (
              intl.formatMessage({id: 'GET_RATE'})
            )}
          </button>
        </div>
      )
    },
    [intl, statesGlobal?.carriers, loadingCarriers, canGetRates, handleFetchCarrierRates]
  )

  const handleLearnMoreClick = (e) => {
    e.preventDefault()
    setShowUspsFirstDisclaimer(true)
  }

  const renderItemService = (item, className = '', index = 0) => {
    if (!item) return null

    // Timeout errors get their own dedicated card
    if (item?.isTimeout) {
      return renderTimeoutCard(item)
    }

    const serviceInfo = SERVICE_INFO[item?.carrier_service]
    const displayName = normalizeRateText(item?.display_name)

    return (
      <div className='rate-card-animate' style={{animationDelay: `${Math.min(index, 4) * 40}ms`}}>
        <div
          id={item.carrier_service}
          className={clsx('btn border d-flex justify-content-between my-5', className, {
            'border-primary bg-light-primary': isEqual(item, data?.chooseRate),
            'border-secondary pe-none opacity-75': disabled && !isEqual(item, data?.chooseRate),
            'border-secondary opacity-75 cursor-no-drop': item?.error,
          })}
          onClick={() => !item?.error && handleClickRate(item)}
        >
          <div className='d-flex align-items-center flex-grow-1'>
            <div className='symbol symbol-40px me-5'>
              <span className='symbol-label bg-transparent'>
                {item?.logo && (
                  <img src={item.logo} className='h-100 align-self-center' alt='logo-shipment' />
                )}
              </span>
            </div>
            <div className='flex-grow-1'>
              <div className='d-flex align-items-left mb-1'>
                <span className='service-name text-capitalize text-start me-2'>
                  {displayName}
                  {item?.error && renderErrors(item)}
                </span>
                {item?.package_type?.label && (
                  <span className='badge badge-pill badge-light'>{item?.package_type?.label}</span>
                )}
              </div>
              {serviceInfo && (
                <div className='text-start'>
                  <div className='service-info-description fw-normal mb-1'>
                    {intl.formatMessage({id: serviceInfo.description})}
                  </div>
                  <div className='service-info-delivery-window text-capitalize'>
                    {intl.formatMessage(
                      {id: serviceInfo.deliveryWindow},
                      getDeliveryWindowValues(serviceInfo)
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          {item?.rate && (
            <div className='d-flex align-items-center'>
              <span className='fs-4 fw-boldest py-4 px-4'>{convertCurrency(item?.rate)}</span>
            </div>
          )}
        </div>
        {data?.chooseRate?.carrier_service === SERVICES.USPS_FIRSTCLASS &&
          item?.carrier_service === SERVICES.USPS_FIRSTCLASS && (
            <div
              className='px-3'
              style={{marginTop: '-1.1rem', marginLeft: '-0.8rem', marginBottom: '0.9rem'}}
            >
              <span className='text-muted fs-7'>
                <span className='text-danger me-1'>*</span>
                {intl.formatMessage({id: 'USPS_FIRST_LIMIT'})}
                <a
                  href='/'
                  className='text-primary ms-1 fw-semibold text-decoration-underline'
                  onClick={handleLearnMoreClick}
                >
                  {intl.formatMessage({id: 'LEARN_MORE'})}
                </a>
              </span>
            </div>
          )}
      </div>
    )
  }

  const renderItemServiceShipped = useMemo(() => {
    const latestShipment: any = last(dataShipment?.shipments) || null
    if (!latestShipment) return null

    const serviceKey = latestShipment?.carrier + '_' + latestShipment?.service
    let service = carriers?.[serviceKey] ?? []
    if (!latestShipment || !service) return null

    const serviceInfo = SERVICE_INFO[serviceKey]

    return (
      <div>
        <div
          className={clsx(
            'btn border d-flex justify-content-between my-5 border-secondary pe-none'
          )}
        >
          <div className='d-flex align-items-center flex-grow-1'>
            <div className='symbol symbol-40px me-5'>
              <span className='symbol-label bg-transparent'>
                <img
                  src={service?.visuals?.logo}
                  className='h-100 align-self-center'
                  alt='logo-shipment'
                />
              </span>
            </div>
            <div className='flex-grow-1'>
              <div className='d-flex align-items-center mb-1'>
                <span className='service-name text-capitalize text-start me-2'>
                  {service?.visuals?.display_name}
                  {service?.error && renderErrors(service)}
                </span>
                {service?.package_type?.label && (
                  <span className='badge badge-pill badge-light'>
                    {service?.package_type?.label}
                  </span>
                )}
              </div>
              {serviceInfo && (
                <div className='text-start'>
                  <div className='service-info-description fw-normal mb-1'>
                    {intl.formatMessage({id: serviceInfo.description})}
                  </div>
                  <div className='service-info-delivery-window text-capitalize'>
                    {intl.formatMessage(
                      {id: serviceInfo.deliveryWindow},
                      getDeliveryWindowValues(serviceInfo)
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          {latestShipment?.fees?.items?.[0]?.amount && (
            <div className='d-flex align-items-center'>
              <span className='fs-4 fw-boldest py-4 px-4'>
                {convertCurrency(getShipmentFees(latestShipment?.fees?.items))}
              </span>
            </div>
          )}
        </div>
      </div>
    )
  }, [carriers, dataShipment?.shipments, renderErrors, intl])

  const pinnedServiceKeys = useMemo(() => {
    const keys = new Set<string>()
    if (pinnedRates.cheapest) keys.add(pinnedRates.cheapest.carrier_service)
    if (pinnedRates.fastest) keys.add(pinnedRates.fastest.carrier_service)
    if (pinnedRates.best) keys.add(pinnedRates.best.carrier_service)
    return keys
  }, [pinnedRates])

  const unpinnedRates = useMemo(() => {
    return rateOptions.filter(
      (item) =>
        !item?.matching_package &&
        !pinnedServiceKeys.has(item.carrier_service) &&
        (!activePresetService || item?.carrier_service !== activePresetService)
    )
  }, [rateOptions, pinnedServiceKeys, activePresetService])

  const pricedMoreRates = useMemo(() => {
    return unpinnedRates
      .filter((item) => Number.isFinite(Number(item?.rate)))
      .slice()
      .sort((a, b) => Number(a.rate) - Number(b.rate))
  }, [unpinnedRates])

  const presetCarrierName = presetService ? getCarrierNameFromServiceKey(presetService) : null

  const hiddenMoreRates = useMemo(() => {
    return rateOptions.filter((item) => {
      if (pinnedServiceKeys.has(item.carrier_service)) return false
      if (Number.isFinite(Number(item?.rate))) return false
      // Exclude the preset carrier — it renders in the Preset section
      if (presetCarrierName && item?.carrierName === presetCarrierName) return false
      return true
    })
  }, [pinnedServiceKeys, rateOptions, presetCarrierName])

  const timeoutRates = useMemo(() => {
    return hiddenMoreRates.filter((item) => item?.isTimeout)
  }, [hiddenMoreRates])

  const nonTimeoutHiddenRates = useMemo(() => {
    return hiddenMoreRates.filter((item) => !item?.isTimeout)
  }, [hiddenMoreRates])

  const loadingVisibleRates = useMemo(() => {
    return rateOptions
      .filter((item) => Number.isFinite(Number(item?.rate)))
      .slice()
      .sort((a, b) => Number(a.rate) - Number(b.rate))
  }, [rateOptions])

  const hasAnyPricedRates = useMemo(() => {
    return loadingVisibleRates.length > 0
  }, [loadingVisibleRates.length])

  const pinnedSection = useMemo(() => {
    const items = [
      pinnedRates.cheapest ? {label: 'cheapest', item: pinnedRates.cheapest} : null,
      pinnedRates.best ? {label: 'best', item: pinnedRates.best} : null,
      pinnedRates.fastest ? {label: 'fastest', item: pinnedRates.fastest} : null,
    ].filter(Boolean) as {label: string; item: any}[]

    if (!activePresetService) return items
    return items.filter(({item}) => item?.carrier_service !== activePresetService)
  }, [pinnedRates, activePresetService])

  // Auto-expand "More Rates" when there are no pinned rates to show
  // (e.g. only excluded services returned valid prices)
  useEffect(() => {
    if (
      !isLoadingRates &&
      hasAnyPricedRates &&
      pinnedSection.length === 0 &&
      !hasPreselected &&
      isPresetServiceApplicable
    ) {
      setIsExpandedAllRates(true)
      setHasEverExpanded(true)
    }
  }, [
    isLoadingRates,
    hasAnyPricedRates,
    pinnedSection.length,
    hasPreselected,
    isPresetServiceApplicable,
  ])

  // Disabled carriers that haven't been fetched yet (still show "Get Rate" button).
  // Stays visible while loading so the spinner shows on the button.
  // Once fetched and done loading, results render in the main rate sections.
  // Carriers being retried are excluded — the timeout card handles the spinner.
  const unfetchedDisabledCarriers = useMemo(() => {
    return disabledCarriers.filter((name) => {
      // Already fetched and done loading — results are in the main sections
      if (fetchedCarriers.has(name) && !loadingCarriers.includes(name)) return false
      // Currently retrying — timeout card handles the spinner
      if (retryingCarriers.includes(name)) return false
      // Preset carrier renders in the Preset section
      if (presetCarrierName && name === presetCarrierName) return false
      return true
    })
  }, [disabledCarriers, fetchedCarriers, loadingCarriers, retryingCarriers, presetCarrierName])

  // Fetched carriers (enabled or disabled) whose results are not visible in any rate section.
  // Shows an inline "no rates" message so users know the fetch completed.
  // When hideNoRates is true, we suppress these empty cards entirely.
  const emptyFetchedCarriers = useMemo(() => {
    if (hideNoRates) return []
    const allFetched = Array.from(fetchedCarriers)
    // Carrier names that already have a visible timeout card
    const timeoutCarrierNames = new Set(
      timeoutRates.map((item) => item?.carrierName).filter(Boolean)
    )
    return allFetched.filter((name) => {
      // Skip carriers not applicable for the current route type
      if (!isCarrierApplicableForRoute(statesGlobal?.carriers, name, isInternational)) return false
      // Skip carriers still loading
      if (loadingCarriers.includes(name)) return false
      // Skip the preset carrier — it renders in the Preset section
      if (presetCarrierName && name === presetCarrierName) return false
      // Skip carriers with a visible timeout card
      if (timeoutCarrierNames.has(name)) return false
      // Skip carriers with priced rates (always visible)
      const hasPricedRates = rateOptions.some(
        (item) =>
          item?.carrier_service?.startsWith(name + '_') &&
          item?.carrier_service !== `${name}_error` &&
          Number.isFinite(Number(item?.rate))
      )
      if (hasPricedRates) return false
      // Skip carriers with error entries — error cards always render
      const hasErrorEntry = rateOptions.some(
        (item) => item?.carrier_service === `${name}_error` && item?.error
      )
      if (hasErrorEntry) return false
      // Skip carriers with unpriced entries (those are visible when hideNoRates is off)
      const hasUnpricedEntry = rateOptions.some(
        (item) =>
          item?.carrier_service?.startsWith(name + '_') && item?.carrier_service !== `${name}_error`
      )
      if (hasUnpricedEntry) return false
      // Carrier was fetched but has no visible card — show empty card
      return true
    })
  }, [
    fetchedCarriers,
    loadingCarriers,
    rateOptions,
    hideNoRates,
    presetCarrierName,
    timeoutRates,
    isInternational,
    statesGlobal?.carriers,
  ])

  // Whether there are active (opted-in) carriers that haven't been fetched yet.
  // Used to determine if "All Rates" should trigger a fetch, and if the button should show.
  const hasUnfetchedEnabledCarriers = useMemo(() => {
    if (!activePresetService || !isPresetServiceApplicable || hasEverExpanded) return false
    // If any non-preset carrier has already been fetched, all active carriers were loaded
    const hasNonPresetFetched = Array.from(fetchedCarriers).some(
      (name) => name !== presetCarrierName
    )
    return !hasNonPresetFetched
  }, [
    activePresetService,
    isPresetServiceApplicable,
    hasEverExpanded,
    fetchedCarriers,
    presetCarrierName,
  ])

  // Rates that would be revealed by clicking "All Rates"
  const hasHiddenRates = useMemo(() => {
    const hasMorePriced = pricedMoreRates.length > 0
    const hasHiddenErrors = !hideNoRates && nonTimeoutHiddenRates.some((item) => !item?.error)
    const hasVisibleErrors = nonTimeoutHiddenRates.some((item) => item?.error)
    const hasTimeouts = timeoutRates.length > 0
    const hasPinnedToReveal = !hasEverExpanded && pinnedSection.length > 0 && hasPreselected
    const hasPinnedRevealed = hasEverExpanded && pinnedSection.length > 0 && hasPreselected
    const hasDisabledCarriers = unfetchedDisabledCarriers.length > 0
    const hasEmptyFetched = emptyFetchedCarriers.length > 0
    // When preset is inapplicable, show "All Rates" so user can dismiss the preset message
    const hasInapplicablePreset =
      !!presetService && !isPresetServiceApplicable && !inapplicablePresetDismissed

    return (
      hasMorePriced ||
      hasHiddenErrors ||
      hasVisibleErrors ||
      hasTimeouts ||
      hasPinnedToReveal ||
      hasPinnedRevealed ||
      hasDisabledCarriers ||
      hasEmptyFetched ||
      hasInapplicablePreset ||
      hasUnfetchedEnabledCarriers
    )
  }, [
    pricedMoreRates,
    hideNoRates,
    nonTimeoutHiddenRates,
    timeoutRates,
    pinnedSection,
    hasPreselected,
    hasEverExpanded,
    unfetchedDisabledCarriers,
    emptyFetchedCarriers,
    isPresetServiceApplicable,
    inapplicablePresetDismissed,
    presetService,
    hasUnfetchedEnabledCarriers,
  ])

  const showRateResults = !isLoadingRates && dataShipment?.status !== OrderStatus.SHIPPED

  // When true, only the NO_RATES_HIDDEN empty state should render — hide all other rate sections
  const showNoRatesHidden =
    showRateResults &&
    (hideNoRates || (Array.isArray(data?.options) && data.options.length === 0)) &&
    !hasAnyPricedRates &&
    !hasExploredAll &&
    !activePresetService &&
    data?.options !== undefined &&
    emptyFetchedCarriers.length === 0

  return (
    <div className={clsx({'cursor-no-drop': disabled})}>
      {dataShipment?.status === OrderStatus.SHIPPED && renderItemServiceShipped}

      {isLoadingRates && dataShipment?.status !== OrderStatus.SHIPPED && (
        <>
          {loadingVisibleRates.map((item, idx) => (
            <React.Fragment
              key={`${item?.carrier_service || 'rate'}:${item?.package_type?.value || idx}:loading`}
            >
              {renderItemService(item, '', idx)}
            </React.Fragment>
          ))}
        </>
      )}

      {showRateResults &&
        !hasAnyPricedRates &&
        !activePresetService &&
        data?.options !== undefined &&
        (hiddenMoreRates.length > 0 || emptyFetchedCarriers.length > 0) && (
          <>
            {hiddenMoreRates
              .filter((item) => {
                if (!hideNoRates) return true
                // When hideNoRates is on, only show timeouts/errors after explore all is clicked
                if (item?.isTimeout) return hasExploredAll
                if (item?.error) return hasExploredAll
                return false
              })
              .map((item, idx) => (
                <React.Fragment
                  key={`${item?.carrier_service || 'rate'}:${
                    item?.package_type?.value || idx
                  }:empty-hidden`}
                >
                  {renderItemService(item, '', idx)}
                </React.Fragment>
              ))}

            {emptyFetchedCarriers.map((carrierName) => renderEmptyFetchedCard(carrierName))}
          </>
        )}

      {showNoRatesHidden && (
        <EmptyRate titleId='NO_RATES_HIDDEN' subTextId='NO_RATES_HIDDEN_SUB_TEXT'>
          {onExploreAllRates && disabledCarriers.length > 0 && (
            <button
              type='button'
              className={clsx(
                'rates-toggle-button bg-transparent border-0 rounded fw-bolder px-4 py-2',
                {'pe-none': !canGetRates}
              )}
              onClick={() => {
                setHasExploredAll(true)
                onExploreAllRates()
              }}
              disabled={!canGetRates}
            >
              <span className='fs-6'>{intl.formatMessage({id: 'EXPLORE_ALL_RATES'})}</span>
            </button>
          )}
        </EmptyRate>
      )}

      {/* After "Explore all rates" was clicked but still no valid rates from any carrier */}
      {showRateResults &&
        hasExploredAll &&
        !hasAnyPricedRates &&
        !isLoadingRates &&
        hiddenMoreRates.length === 0 &&
        emptyFetchedCarriers.length === 0 &&
        data?.options !== undefined && (
          <EmptyRate titleId='NO_RATES_HIDDEN' subTextId='NO_RATES_AVAILABLE' />
        )}

      {showRateResults && !presetTransitioning && presetService && presetCarrierName && (
        <>
          {/* Preset rate found and service is applicable — show the selected rate */}
          {hasPreselected && isPresetServiceApplicable && hasAnyPricedRates && (
            <>
              <span className='fw-bolder fw-bold fs-7'>{intl.formatMessage({id: 'PRESET'})}</span>
              {renderItemService(preselectedRate, '', 0)}
            </>
          )}

          {/* Preset rate not found or not applicable — show PresetRateSection only on initial view.
              Once the user clicks "All Rates" / "Less Rates" (hasEverExpanded), hide it.
              For inapplicable presets, only show once per preset application. */}
          {(!hasPreselected || !isPresetServiceApplicable) &&
            (!isPresetServiceApplicable ? !inapplicablePresetDismissed : !hasEverExpanded) &&
            !showNoRatesHidden &&
            data?.options !== undefined && (
              <PresetRateSection
                presetService={presetService}
                presetCarrierName={presetCarrierName}
                carriers={carriers}
                rateOptions={rateOptions}
                isInternational={isInternational}
                loadingCarriers={loadingCarriers}
                retryingCarriers={retryingCarriers}
                fetchedCarriers={fetchedCarriers}
                renderTimeoutCard={renderTimeoutCard}
                renderItemService={renderItemService}
              />
            )}
        </>
      )}

      {showRateResults && hasAnyPricedRates && hasEverExpanded && size(matchingPackages) > 0 && (
        <>
          <span className='fw-bolder fw-bold fs-7'>
            {intl.formatMessage({
              id: matchingPackages[0]?.package_type?.value?.includes('cubic')
                ? 'CUBIC_RATE'
                : 'FLAT_RATE',
            })}
          </span>
          <div>
            {matchingPackages.map((item, idx) => (
              <React.Fragment
                key={`${item?.carrier_service || 'rate'}:${item?.package_type?.value || idx}`}
              >
                {renderItemService(item, '', idx)}
              </React.Fragment>
            ))}
          </div>
        </>
      )}

      {showRateResults && hasAnyPricedRates && hasEverExpanded && pinnedSection.length > 0 && (
        <>
          {pinnedSection.map(({label, item}, index) => (
            <div key={label}>
              <span className='fw-bolder fw-bold fs-7'>
                {intl.formatMessage({id: label.toUpperCase()})}
              </span>
              {renderItemService(item, '', index)}
            </div>
          ))}
        </>
      )}

      {showRateResults &&
        !showNoRatesHidden &&
        (hasAnyPricedRates || activePresetService) &&
        !isExpandedAllRates &&
        hasHiddenRates &&
        data?.options !== undefined && (
          <div className='text-center my-5'>
            <button
              type='button'
              className='rates-toggle-button bg-transparent border-0 rounded fw-bolder p-5 w-100'
              onClick={() => {
                toggleAllRates()
                userExpandedRef.current = true
                setInapplicablePresetDismissed(true)
                if (onFetchActiveCarriers && !hasEverExpanded && hasUnfetchedEnabledCarriers) {
                  onFetchActiveCarriers()
                }
              }}
            >
              <span className='fs-5'>{intl.formatMessage({id: 'ALL_RATES'})}</span>
              <KTSVG path='/media/gori/arrows/arrow_down.svg' small={false} />
            </button>
          </div>
        )}

      {showRateResults &&
        isExpandedAllRates &&
        (hasAnyPricedRates || activePresetService) &&
        data?.options !== undefined && (
          <>
            {(pricedMoreRates.length > 0 ||
              timeoutRates.length > 0 ||
              emptyFetchedCarriers.length > 0 ||
              unfetchedDisabledCarriers.length > 0 ||
              nonTimeoutHiddenRates.some((item) => item?.error) ||
              (!hideNoRates && nonTimeoutHiddenRates.some((item) => !item?.error))) && (
              <span className='fw-bolder fw-bold fs-7'>
                {intl.formatMessage({id: 'MORE_RATES'})}
              </span>
            )}
            {pricedMoreRates.map((item, idx) => (
              <React.Fragment
                key={`${item?.carrier_service || 'rate'}:${item?.package_type?.value || idx}`}
              >
                {renderItemService(item, '', idx)}
              </React.Fragment>
            ))}

            {!hideNoRates &&
              nonTimeoutHiddenRates
                .filter((item) => !item?.error)
                .map((item, idx) => (
                  <React.Fragment
                    key={`${item?.carrier_service || 'rate'}:${
                      item?.package_type?.value || idx
                    }:hidden`}
                  >
                    {renderItemService(item, '', pricedMoreRates.length + idx)}
                  </React.Fragment>
                ))}

            {nonTimeoutHiddenRates
              .filter((item) => item?.error)
              .map((item, idx) => (
                <React.Fragment
                  key={`${item?.carrier_service || 'rate'}:${
                    item?.package_type?.value || idx
                  }:error`}
                >
                  {renderItemService(item, '', pricedMoreRates.length + idx)}
                </React.Fragment>
              ))}

            {timeoutRates.map((item, idx) => (
              <React.Fragment
                key={`${item?.carrier_service || 'rate'}:${
                  item?.package_type?.value || idx
                }:timeout`}
              >
                {renderTimeoutCard(item)}
              </React.Fragment>
            ))}

            {emptyFetchedCarriers.map((carrierName) => renderEmptyFetchedCard(carrierName))}

            {unfetchedDisabledCarriers.length > 0 &&
              data?.options !== undefined &&
              unfetchedDisabledCarriers.map((carrierName) =>
                renderDisabledCarrierCard(carrierName)
              )}
          </>
        )}

      {showRateResults &&
        !showNoRatesHidden &&
        !isExpandedAllRates &&
        !hideNoRates &&
        !hasAnyPricedRates &&
        !activePresetService &&
        unfetchedDisabledCarriers.length > 0 &&
        data?.options !== undefined && (
          <>
            {unfetchedDisabledCarriers.map((carrierName) => renderDisabledCarrierCard(carrierName))}
          </>
        )}

      {showRateResults && isExpandedAllRates && hasHiddenRates && data?.options !== undefined && (
        <div className='text-center my-5'>
          <button
            type='button'
            className='rates-toggle-button bg-transparent border-0 rounded fw-bolder p-5 w-100'
            onClick={() => {
              toggleAllRates()
              userExpandedRef.current = false
            }}
          >
            <span className='fs-5'>{intl.formatMessage({id: 'LESS_RATES'})}</span>
            <KTSVG path='/media/gori/arrows/arrow_up.svg' small={false} />
          </button>
        </div>
      )}

      {showUspsFirstDisclaimer && (
        <div
          className='position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center'
          style={{
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1050,
          }}
          onClick={() => setShowUspsFirstDisclaimer(false)}
        >
          <div className='rate-usps-first-disclaimer' onClick={(e) => e.stopPropagation()}>
            <div className='d-flex justify-content-between align-items-center mb-4'>
              <h3 className='disclaimer-title'>
                {intl.formatMessage({id: 'USPS_FIRST_RATE_DISCLAIMER_TITLE'})}
              </h3>
              <button
                className='btn-close fs-3'
                onClick={() => setShowUspsFirstDisclaimer(false)}
              ></button>
            </div>

            <hr className='mt-8 mb-16' />

            <div className='mb-4'>
              <h5 className='disclaimer-sub-title'>
                {intl.formatMessage({id: 'USPS_FIRST_RATE_DISCLAIMER_SUBTITLE_1'})}
              </h5>
              <p className='disclaimer-text'>
                {intl.formatMessage({id: 'USPS_FIRST_RATE_DISCLAIMER_TEXT_1'})}{' '}
              </p>

              <h5 className='disclaimer-sub-title'>
                {intl.formatMessage({id: 'USPS_FIRST_RATE_DISCLAIMER_SUBTITLE_2'})}
              </h5>
              <p className='disclaimer-text'>
                {intl.formatMessage({id: 'USPS_FIRST_RATE_DISCLAIMER_TEXT_2'})}{' '}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export {Rates}
